import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  SP80022Parameters,
  SP80090BParameters,
  TunableBiasParameters,
  GaussianParameters,
} from '@/types'
import type { ParseResult } from '@/lib/fileParser'
import {
  DEFAULT_SP80022_PARAMS,
  DEFAULT_SP80090B_PARAMS,
  DEFAULT_TUNABLE_BIAS_PARAMS,
  DEFAULT_GAUSSIAN_PARAMS,
} from '@/lib/constants'

// ---------------------------------------------------------------------------
// Serialization helpers for typed arrays
// ---------------------------------------------------------------------------

interface SerializedParseResult {
  kind: 'binary' | 'symbol' | 'continuous'
  input: {
    data: number[]          // plain array for JSON serialization
    format: string
    sampleCount: number
    fileName: string
    fileSize: number
    sha256?: string
    bitLength?: number      // binary only
    symbolSize?: number     // symbol only
    alphabetSize?: number   // symbol only
  }
}

function serializeInput(result: ParseResult): SerializedParseResult {
  const base = {
    format: result.input.format,
    sampleCount: result.input.sampleCount,
    fileName: result.input.fileName,
    fileSize: result.input.fileSize,
    sha256: result.input.sha256,
  }
  if (result.kind === 'binary') {
    return {
      kind: 'binary',
      input: { ...base, data: Array.from(result.input.data), bitLength: result.input.bitLength },
    }
  }
  if (result.kind === 'symbol') {
    return {
      kind: 'symbol',
      input: {
        ...base,
        data: Array.from(result.input.data),
        symbolSize: result.input.symbolSize,
        alphabetSize: result.input.alphabetSize,
      },
    }
  }
  // continuous
  return {
    kind: 'continuous',
    input: { ...base, data: Array.from(result.input.data) },
  }
}

function deserializeInput(s: SerializedParseResult): ParseResult {
  if (s.kind === 'binary') {
    return {
      kind: 'binary',
      input: {
        data: new Uint8Array(s.input.data),
        bitLength: s.input.bitLength!,
        format: s.input.format as any,
        sampleCount: s.input.sampleCount,
        fileName: s.input.fileName,
        fileSize: s.input.fileSize,
        sha256: s.input.sha256,
      },
    }
  }
  if (s.kind === 'symbol') {
    return {
      kind: 'symbol',
      input: {
        data: new Uint8Array(s.input.data),
        symbolSize: s.input.symbolSize!,
        alphabetSize: s.input.alphabetSize!,
        format: s.input.format as any,
        sampleCount: s.input.sampleCount,
        fileName: s.input.fileName,
        fileSize: s.input.fileSize,
        sha256: s.input.sha256,
      },
    }
  }
  return {
    kind: 'continuous',
    input: {
      data: new Float64Array(s.input.data),
      format: s.input.format as any,
      sampleCount: s.input.sampleCount,
      fileName: s.input.fileName,
      fileSize: s.input.fileSize,
      sha256: s.input.sha256,
    },
  }
}

// ---------------------------------------------------------------------------
// Persisted state shape
// ---------------------------------------------------------------------------

type ModuleKey = 'sp80022' | 'sp80090b' | 'tunableBias' | 'gaussian'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SerializedResult = any

interface PersistedState {
  // Per-module input data
  sp80022Input: SerializedParseResult | null
  sp80090bInput: SerializedParseResult | null
  tunableBiasInput: SerializedParseResult | null
  gaussianInput: SerializedParseResult | null

  // Params
  sp80022Params: SP80022Parameters | null
  sp80090bParams: SP80090BParameters | null
  tunableBiasParams: TunableBiasParameters | null
  gaussianParams: GaussianParameters | null

  // Results
  sp80022Result: SerializedResult | null
  sp80090bResult: SerializedResult | null
  tunableBiasResult: SerializedResult | null
  gaussianResult: SerializedResult | null

  // PIT Bridge (Gaussian page)
  pitQuantization: '1bit' | '8bit' | '32bit'
  pitData: { data: number[]; bitLength: number } | null  // serialized Uint8Array/Uint32Array
  pitSP22Result: SerializedResult | null
  pitSP90BResult: SerializedResult | null

  // Multi-dataset comparison snapshots
  savedSnapshots: SavedSnapshot[]
}

export interface SavedSnapshot {
  id: string
  name: string
  timestamp: number
  module: ModuleKey
  fileName: string
  sampleCount: number
  result: SerializedResult
}

interface PersistedActions {
  // Input data (per module)
  setModuleInput: (module: ModuleKey, data: ParseResult) => void
  getModuleInput: (module: ModuleKey) => ParseResult | null
  clearModuleInput: (module: ModuleKey) => void
  copyInputFrom: (source: ModuleKey, target: ModuleKey) => void
  getAvailableInputs: () => Array<{ module: ModuleKey; label: string; kind: string; fileName: string; sampleCount: number }>

  // SP 800-22
  setSP80022Params: (p: SP80022Parameters) => void
  setSP80022Result: (r: SerializedResult) => void
  clearSP80022Result: () => void

  // SP 800-90B
  setSP80090BParams: (p: SP80090BParameters) => void
  setSP80090BResult: (r: SerializedResult) => void
  clearSP80090BResult: () => void

  // Tunable-Bias
  setTunableBiasParams: (p: TunableBiasParameters) => void
  setTunableBiasResult: (r: SerializedResult) => void
  clearTunableBiasResult: () => void

  // Gaussian
  setGaussianParams: (p: GaussianParameters) => void
  setGaussianResult: (r: SerializedResult) => void
  clearGaussianResult: () => void

  // PIT Bridge
  setPitQuantization: (q: '1bit' | '8bit' | '32bit') => void
  setPitData: (data: Uint8Array | Uint32Array, bitLength: number) => void
  clearPitData: () => void
  setPitSP22Result: (r: SerializedResult) => void
  setPitSP90BResult: (r: SerializedResult) => void
  clearPitResults: () => void
  getPitData: () => { data: Uint8Array | Uint32Array; bitLength: number } | null

  clearAllResults: () => void

  // Multi-dataset snapshots
  saveSnapshot: (module: ModuleKey, name: string) => string | null
  deleteSnapshot: (id: string) => void
  getSnapshots: (module?: ModuleKey) => SavedSnapshot[]
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const defaultSP80022Params: SP80022Parameters = { ...DEFAULT_SP80022_PARAMS }
export const defaultSP80090BParams: SP80090BParameters = { ...DEFAULT_SP80090B_PARAMS }
export const defaultTunableBiasParams: TunableBiasParameters = { ...DEFAULT_TUNABLE_BIAS_PARAMS }
export const defaultGaussianParams: GaussianParameters = { ...DEFAULT_GAUSSIAN_PARAMS }

const MODULE_LABELS: Record<ModuleKey, string> = {
  sp80022: 'SP 800-22',
  sp80090b: 'SP 800-90B',
  tunableBias: 'Tunable-Bias',
  gaussian: 'Gaussian',
}

function inputField(module: ModuleKey): keyof PersistedState {
  return `${module}Input` as keyof PersistedState
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePersistStore = create<PersistedState & PersistedActions>()(
  persist(
    (set, get) => ({
      // Initial state
      sp80022Input: null,
      sp80090bInput: null,
      tunableBiasInput: null,
      gaussianInput: null,
      sp80022Params: null,
      sp80022Result: null,
      sp80090bParams: null,
      sp80090bResult: null,
      tunableBiasParams: null,
      tunableBiasResult: null,
      gaussianParams: null,
      gaussianResult: null,
      pitQuantization: '1bit',
      pitData: null,
      pitSP22Result: null,
      pitSP90BResult: null,
      savedSnapshots: [],

      // Input data
      setModuleInput: (module, data) => {
        set({ [inputField(module)]: serializeInput(data) })
      },
      getModuleInput: (module) => {
        const s = get()[inputField(module)] as SerializedParseResult | null
        if (!s) return null
        try { return deserializeInput(s) } catch { return null }
      },
      clearModuleInput: (module) => {
        set({ [inputField(module)]: null })
      },
      copyInputFrom: (source, target) => {
        const s = get()[inputField(source)] as SerializedParseResult | null
        if (s) set({ [inputField(target)]: s })
      },
      getAvailableInputs: () => {
        const state = get()
        const result: Array<{ module: ModuleKey; label: string; kind: string; fileName: string; sampleCount: number }> = []
        for (const key of ['sp80022', 'sp80090b', 'tunableBias', 'gaussian'] as ModuleKey[]) {
          const s = state[inputField(key)] as SerializedParseResult | null
          if (s) {
            result.push({
              module: key,
              label: MODULE_LABELS[key],
              kind: s.kind,
              fileName: s.input.fileName,
              sampleCount: s.input.sampleCount,
            })
          }
        }
        return result
      },

      // SP 800-22
      setSP80022Params: (p) => set({ sp80022Params: p }),
      setSP80022Result: (r) => set({ sp80022Result: r }),
      clearSP80022Result: () => set({ sp80022Result: null }),

      // SP 800-90B
      setSP80090BParams: (p) => set({ sp80090bParams: p }),
      setSP80090BResult: (r) => set({ sp80090bResult: r }),
      clearSP80090BResult: () => set({ sp80090bResult: null }),

      // Tunable-Bias
      setTunableBiasParams: (p) => set({ tunableBiasParams: p }),
      setTunableBiasResult: (r) => set({ tunableBiasResult: r }),
      clearTunableBiasResult: () => set({ tunableBiasResult: null }),

      // Gaussian
      setGaussianParams: (p) => set({ gaussianParams: p }),
      setGaussianResult: (r) => set({ gaussianResult: r }),
      clearGaussianResult: () => set({ gaussianResult: null }),

      // PIT Bridge
      setPitQuantization: (q) => set({ pitQuantization: q, pitData: null, pitSP22Result: null, pitSP90BResult: null }),
      setPitData: (data, bitLength) => set({ pitData: { data: Array.from(data), bitLength } }),
      clearPitData: () => set({ pitData: null, pitSP22Result: null, pitSP90BResult: null }),
      setPitSP22Result: (r) => set({ pitSP22Result: r }),
      setPitSP90BResult: (r) => set({ pitSP90BResult: r }),
      clearPitResults: () => set({ pitSP22Result: null, pitSP90BResult: null }),
      getPitData: () => {
        const s = get().pitData
        if (!s) return null
        const q = get().pitQuantization
        const data = q === '32bit' ? new Uint32Array(s.data) : new Uint8Array(s.data)
        return { data, bitLength: s.bitLength }
      },

      clearAllResults: () =>
        set({
          sp80022Result: null,
          sp80090bResult: null,
          tunableBiasResult: null,
          gaussianResult: null,
          pitData: null,
          pitSP22Result: null,
          pitSP90BResult: null,
        }),

      // Multi-dataset snapshots
      saveSnapshot: (module: ModuleKey, name: string) => {
        const state = get()
        const resultKey = `${module}Result` as keyof PersistedState
        const inputKey = `${module}Input` as keyof PersistedState
        const result = state[resultKey]
        const input = state[inputKey] as SerializedParseResult | null
        if (!result) return null
        const id = `snap_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
        const snapshot: SavedSnapshot = {
          id,
          name,
          timestamp: Date.now(),
          module,
          fileName: input?.input?.fileName ?? 'unknown',
          sampleCount: input?.input?.sampleCount ?? 0,
          result,
        }
        set({ savedSnapshots: [...state.savedSnapshots, snapshot] })
        return id
      },
      deleteSnapshot: (id: string) => {
        set({ savedSnapshots: get().savedSnapshots.filter((s) => s.id !== id) })
      },
      getSnapshots: (module?: ModuleKey) => {
        const all = get().savedSnapshots
        return module ? all.filter((s) => s.module === module) : all
      },
    }),
    {
      name: 'bitassay-state',
      version: 2,
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('[BitAssay] Failed to rehydrate persist store:', error)
          } else if (state) {
            console.log('[BitAssay] Rehydrated persist store:', {
              sp80022Result: state.sp80022Result ? 'present' : 'null',
              sp80090bResult: state.sp80090bResult ? 'present' : 'null',
              tunableBiasResult: state.tunableBiasResult ? 'present' : 'null',
              gaussianResult: state.gaussianResult ? 'present' : 'null',
              sp80022Input: state.sp80022Input ? 'present' : 'null',
            })
          }
        }
      },
      migrate: (persisted: any, version: number) => {
        // Preserve all existing data when upgrading schema versions
        if (version < 2) {
          return {
            ...persisted,
            pitQuantization: '1bit',
            pitData: null,
            pitSP22Result: null,
            pitSP90BResult: null,
            sp80022Input: persisted?.sp80022Input ?? null,
            sp80090bInput: persisted?.sp80090bInput ?? null,
            tunableBiasInput: persisted?.tunableBiasInput ?? null,
            gaussianInput: persisted?.gaussianInput ?? null,
          }
        }
        return persisted as any
      },
    },
  ),
)
